import { Card as AntCard } from 'antd';
import type { CardProps as AntCardProps } from 'antd/es/card';
import { forwardRef } from 'react';
import { usePermissionStore } from '../../../stores/permission.store';
import './Card.css';

export interface CardProps extends Omit<AntCardProps, 'variant' | 'hoverable'> {
  /**
   * Enable hover effect
   * @default false
   */
  hoverable?: boolean;
  /**
   * Card variant
   * @default 'default'
   */
  variant?: 'default' | 'bordered' | 'elevated';
  /**
   * Permission(s) required to render the card.
   * If the user lacks permission, the card is not rendered.
   */
  permission?: string | string[];
  /**
   * When true, ALL listed permissions are required. Default: any one is enough.
   */
  requireAll?: boolean;
}

/**
 * Modern Card Component
 *
 * Container with shadow and rounded corners, with built-in permission gating.
 *
 * @example
 * ```tsx
 * <Card title="Card Title" permission="iam:user:view" variant="elevated" hoverable>
 *   <p>Card content</p>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', hoverable = false, permission, requireAll = false, className, ...props }, ref) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissionStore();

    if (permission) {
      const perms = Array.isArray(permission) ? permission : [permission];
      const allowed = requireAll
        ? hasAllPermissions(perms)
        : perms.length === 1
          ? hasPermission(perms[0]!)
          : hasAnyPermission(perms);
      if (!allowed) return null;
    }

    const classNames = [
      'modern-card',
      `modern-card--${variant}`,
      hoverable && 'modern-card--hoverable',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return <AntCard ref={ref} hoverable={hoverable} className={classNames} {...props} />;
  }
);

Card.displayName = 'Card';
